import React, { useState, useEffect } from 'react';
import { Box, VStack, Heading, Text, Button, SimpleGrid, Spinner, useToast, Tag, HStack, Avatar } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { useApp } from '../../context/AppContext';

const MotionBox = motion(Box);

const MensalistasListPage = () => {
  const [mensalistas, setMensalistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useApp();

  // Verificar se o usuário é administrador
  useEffect(() => {
    if (!isAdmin) {
      navigate('/menu');
      toast({
        title: 'Acesso restrito',
        description: 'Você não tem permissão para acessar esta página.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [isAdmin, navigate, toast]);

  // Buscar mensalistas
  useEffect(() => {
    const fetchMensalistas = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('mensalistas')
        .select('*, pacotes_mensalistas(nome)')
        .order('nome');

      if (error) {
        console.error('Erro ao buscar mensalistas:', error);
        toast({
          title: 'Erro ao carregar mensalistas',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setMensalistas(data || []);
      }
      setLoading(false);
    };

    fetchMensalistas();
  }, [toast]);

  const handleBack = () => {
    navigate('/menu');
  };

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      minHeight="100vh"
      width="100%"
      bg="black"
      p={6}
    >
      <VStack spacing={6} align="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <Heading color="yellow.400" size="xl">Mensalistas</Heading>
          <Button colorScheme="gray" variant="outline" onClick={handleBack}>
            Voltar ao Menu
          </Button>
        </HStack>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minH="300px">
            <Spinner size="xl" color="yellow.400" thickness="4px" />
          </Box>
        ) : (
          <>
            <Text color="gray.300" fontSize="lg" mb={4}>
              Total de mensalistas: {mensalistas.length}
            </Text>
            
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {mensalistas.map((mensalista) => (
                <Box
                  key={mensalista.id}
                  bg="gray.800"
                  borderRadius="lg"
                  p={5}
                  boxShadow="lg"
                  borderWidth="1px"
                  borderColor="gray.700"
                  _hover={{ borderColor: 'yellow.500', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  <HStack spacing={4} mb={3}>
                    <Avatar 
                      size="lg" 
                      name={mensalista.nome} 
                      src={mensalista.foto_url} 
                      bg="yellow.400"
                      color="black"
                    />
                    <VStack align="start" spacing={1}>
                      <Heading size="md" color="white">{mensalista.nome}</Heading>
                      <Tag colorScheme={mensalista.status === 'ativo' ? 'green' : 'red'}>
                        {mensalista.status === 'ativo' ? 'Ativo' : 'Inativo'}
                      </Tag>
                    </VStack>
                  </HStack>
                  
                  <Text color="gray.300" mb={2}>
                    <strong>Pacote:</strong> {mensalista.pacotes_mensalistas?.nome || 'Nenhum'}
                  </Text>
                  
                  <Text color="gray.300" mb={2}>
                    <strong>Telefone:</strong> {mensalista.telefone || 'Não informado'}
                  </Text>
                  
                  {mensalista.data_inicio && mensalista.data_fim && (
                    <Text color="gray.300" mb={2}>
                      <strong>Vigência:</strong> {new Date(mensalista.data_inicio).toLocaleDateString()} a {new Date(mensalista.data_fim).toLocaleDateString()}
                    </Text>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          </>
        )}
      </VStack>
    </MotionBox>
  );
};

export default MensalistasListPage;
